import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, parseISO, isFuture, isToday, isSameDay } from 'date-fns';
import { Calendar as CalendarIcon, Clock, Plus, Settings, Link, Trash2, Users, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import { usePageHeader } from '../context/PageHeaderContext';

const CalendarPage = () => {
  const { api, isFeatureEnabled } = useAuth();
  const [searchParams] = useSearchParams();
  // Calendar tab is the default — user request 2026-02. Renamed from
  // "My Calendar" → "Calendar"; previous "My Appointments" → "Upcoming
  // Appointments". Order also flipped (Calendar first).
  const [activeTab, setActiveTab] = useState('calendar');
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedAppointmentId, setExpandedAppointmentId] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  
  const [newAppointment, setNewAppointment] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    attendees: []
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Deep-link: open the Upcoming tab and expand a specific appointment
  // when arriving from a notification like
  // `/calendar?appointmentId={id}` (meeting accepted) or
  // `/calendar?meeting={id}` (legacy fallback).
  useEffect(() => {
    const apptId = searchParams.get('appointmentId') || searchParams.get('meeting');
    if (!apptId || !appointments.length) return;
    const match = appointments.find(
      (a) => a.id === apptId
        || a.meeting_request_id === apptId
        || a.related_id === apptId
    );
    if (match) {
      setActiveTab('appointments');
      setExpandedAppointmentId(match.id);
      // Jump the appointment's date into focus on the Calendar tab too.
      try { setSelectedDate(parseISO(match.start_time)); } catch (_e) { /* noop */ }
    }
  }, [searchParams, appointments]);

  const fetchAppointments = async () => {
    try {
      const res = await api.get('/appointments');
      setAppointments(res.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
    }
  };

  const handleCreateAppointment = async () => {
    if (!newAppointment.title || !newAppointment.start_time || !newAppointment.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/appointments', newAppointment);
      // silent success
      setShowCreateDialog(false);
      setNewAppointment({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        attendees: []
      });
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAppointment = async (id) => {
    try {
      await api.delete(`/appointments/${id}`);
      // silent success
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const handleEditClick = (apt) => {
    setEditingAppointment({
      id: apt.id,
      title: apt.title,
      description: apt.description || '',
      start_time: apt.start_time,
      end_time: apt.end_time,
      attendees: apt.attendees || []
    });
  };

  const handleToggleExpand = (aptId) => {
    if (expandedAppointmentId === aptId) {
      setExpandedAppointmentId(null);
      setEditingAppointment(null);
    } else {
      setExpandedAppointmentId(aptId);
      setEditingAppointment(null);
    }
  };

  const handleUpdateAppointment = async () => {
    if (!editingAppointment.title || !editingAppointment.start_time || !editingAppointment.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/appointments/${editingAppointment.id}`, {
        title: editingAppointment.title,
        description: editingAppointment.description,
        start_time: editingAppointment.start_time,
        end_time: editingAppointment.end_time,
        attendees: editingAppointment.attendees
      });
      // silent success
      setEditingAppointment(null);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    // react-day-picker calls onSelect(undefined) when the user clicks
    // the currently-selected day (deselecting it). We don't allow null
    // selection here — keep the previous date so format() / getAppointmentsForDate
    // never receive an undefined value.
    if (date) setSelectedDate(date);
  };

  const handleCreateForSelectedDate = () => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    setNewAppointment(prev => ({
      ...prev,
      title: '',
      description: '',
      start_time: `${dateStr}T09:00:00`,
      end_time: `${dateStr}T10:00:00`
    }));
    setShowCreateDialog(true);
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(apt => isSameDay(parseISO(apt.start_time), date))
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  };

  const selectedDateAppointments = getAppointmentsForDate(selectedDate);

  const upcomingAppointments = appointments
    .filter(apt => isFuture(parseISO(apt.start_time)) || isToday(parseISO(apt.start_time)))
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  const appointmentDates = appointments.map(apt => format(parseISO(apt.start_time), 'yyyy-MM-dd'));

  // Mobile header override — title "Calendar" + Settings + "+" icons
  // before the bell. Settings + Plus are removed from the page body.
  usePageHeader({
    title: 'Calendar',
    rightSlot: (
      <>
        <button
          onClick={() => setShowCreateDialog(true)}
          className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
          aria-label="New appointment"
          data-testid="header-new-appointment-btn"
        >
          <Plus className="w-5 h-5" />
        </button>
        <button
          onClick={() => setShowSettingsDialog(true)}
          className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
          aria-label="Calendar settings"
          data-testid="header-calendar-settings-btn"
        >
          <Settings className="w-5 h-5" />
        </button>
      </>
    ),
  }, []);

  return (
    <div className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 space-y-4" style={{ top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))', bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))' }} data-testid="calendar-page">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${isFeatureEnabled('calendar_appointments') && isFeatureEnabled('calendar_my_calendar') ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {isFeatureEnabled('calendar_my_calendar') && (
            <TabsTrigger value="calendar" data-testid="calendar-tab">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Calendar
            </TabsTrigger>
          )}
          {isFeatureEnabled('calendar_appointments') && (
            <TabsTrigger value="appointments" data-testid="appointments-tab">
              <Clock className="w-4 h-4 mr-2" />
              Upcoming Appointments
            </TabsTrigger>
          )}
        </TabsList>

        {isFeatureEnabled('calendar_appointments') && (
        <TabsContent value="appointments" className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold">Upcoming Appointments</h2>
            <Button 
              size="sm" 
              onClick={() => setShowCreateDialog(true)}
              data-testid="create-appointment-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
          </div>
          {upcomingAppointments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No upcoming appointments</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {upcomingAppointments.map((apt) => {
                const isExpanded = expandedAppointmentId === apt.id;
                const isEditing = editingAppointment?.id === apt.id;
                
                return (
                  <div
                    key={apt.id}
                    className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden transition-all"
                    data-testid={`appointment-${apt.id}`}
                  >
                    {/* Collapsed View - Always visible */}
                    <div
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                      onClick={() => handleToggleExpand(apt.id)}
                      data-testid={`appointment-header-${apt.id}`}
                    >
                      <div className="w-9 h-9 rounded-full bg-[#586D52]/20 flex items-center justify-center flex-shrink-0">
                        <CalendarIcon className="w-4 h-4 text-[#586D52]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{apt.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(parseISO(apt.start_time), 'MMM d, yyyy • h:mm a')}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                          {format(parseISO(apt.start_time), 'h:mm a')}
                        </Badge>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded View */}
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800">
                        {isEditing ? (
                          /* Edit Mode */
                          <div className="space-y-3 mt-2">
                            <div className="space-y-1">
                              <Label className="text-xs">Title</Label>
                              <Input
                                value={editingAppointment.title}
                                onChange={(e) => setEditingAppointment({...editingAppointment, title: e.target.value})}
                                className="h-8 text-sm"
                                data-testid={`edit-title-${apt.id}`}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Description</Label>
                              <Input
                                value={editingAppointment.description}
                                onChange={(e) => setEditingAppointment({...editingAppointment, description: e.target.value})}
                                className="h-8 text-sm"
                                placeholder="Add a description..."
                                data-testid={`edit-description-${apt.id}`}
                              />
                            </div>
                            <div className="grid grid-cols-1 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs">Start Time</Label>
                                <Input
                                  type="datetime-local"
                                  value={editingAppointment.start_time ? editingAppointment.start_time.slice(0, 16) : ''}
                                  onChange={(e) => setEditingAppointment({...editingAppointment, start_time: e.target.value + ':00'})}
                                  className="h-9 text-sm"
                                  style={{ fontSize: '16px' }}
                                  data-testid={`edit-start-${apt.id}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">End Time</Label>
                                <Input
                                  type="datetime-local"
                                  value={editingAppointment.end_time ? editingAppointment.end_time.slice(0, 16) : ''}
                                  onChange={(e) => setEditingAppointment({...editingAppointment, end_time: e.target.value + ':00'})}
                                  className="h-9 text-sm"
                                  style={{ fontSize: '16px' }}
                                  data-testid={`edit-end-${apt.id}`}
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => setEditingAppointment(null)}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                className="flex-1 bg-[#586D52] hover:bg-[#4a5d46]"
                                onClick={handleUpdateAppointment}
                                disabled={loading}
                                data-testid={`save-edit-${apt.id}`}
                              >
                                {loading ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="space-y-3 mt-2">
                            {apt.description && (
                              <div>
                                <Label className="text-xs text-muted-foreground">Description</Label>
                                <p className="text-sm mt-0.5">{apt.description}</p>
                              </div>
                            )}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs text-muted-foreground">Start</Label>
                                <p className="text-sm mt-0.5">{format(parseISO(apt.start_time), 'MMM d, yyyy h:mm a')}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">End</Label>
                                <p className="text-sm mt-0.5">{format(parseISO(apt.end_time), 'MMM d, yyyy h:mm a')}</p>
                              </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => handleEditClick(apt)}
                                data-testid={`edit-appointment-${apt.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                                Edit
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleDeleteAppointment(apt.id)}
                                data-testid={`delete-appointment-${apt.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
        )}

        {isFeatureEnabled('calendar_my_calendar') && (
        <TabsContent value="calendar" className="mt-4 space-y-4">
          {/* Full-width Calendar */}
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-3" data-testid="calendar-view-card">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              className="w-full"
              classNames={{
                months: "w-full",
                month: "w-full",
                table: "w-full",
                head_row: "flex w-full",
                head_cell: "flex-1 text-center text-muted-foreground text-xs font-medium py-2",
                row: "flex w-full mt-1",
                cell: "flex-1 text-center p-0 relative",
                day: "h-10 md:portrait:h-16 w-full rounded-lg text-sm md:portrait:text-base font-medium hover:bg-[#586D52]/10 transition-colors",
                day_selected: "bg-[#586D52] text-white hover:bg-[#586D52] hover:text-white",
                day_today: "bg-gray-100 dark:bg-gray-800 text-[#586D52] font-bold",
                day_outside: "text-muted-foreground opacity-50",
                nav_button: "h-8 w-8 rounded-lg hover:bg-muted",
                caption: "flex justify-center pt-1 pb-3 relative items-center",
                caption_label: "text-base font-semibold",
              }}
              modifiers={{
                hasAppointment: (date) => appointmentDates.includes(format(date, 'yyyy-MM-dd'))
              }}
              modifiersStyles={{
                hasAppointment: {
                  backgroundColor: 'rgba(88, 109, 82, 0.15)',
                  borderRadius: '8px'
                }
              }}
            />
          </div>

          {/* Daily Schedule Section */}
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-semibold">Daily Schedule</h3>
                <p className="text-xs text-muted-foreground">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</p>
              </div>
              <Button 
                size="sm" 
                className="bg-[#586D52] hover:bg-[#4a5d46]"
                onClick={handleCreateForSelectedDate}
                data-testid="create-for-date-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>

            {selectedDateAppointments.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No appointments for this day</p>
                <p className="text-xs mt-1">Click "Add" to create one</p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedDateAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className="flex items-center gap-3 p-2.5 bg-[#586D52]/10 rounded-lg border border-[#586D52]/20"
                    data-testid={`daily-apt-${apt.id}`}
                  >
                    <div className="flex-shrink-0 text-center">
                      <p className="text-xs font-semibold text-[#586D52]">{format(parseISO(apt.start_time), 'h:mm')}</p>
                      <p className="text-[10px] text-muted-foreground">{format(parseISO(apt.start_time), 'a')}</p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{apt.title}</p>
                      {apt.description && (
                        <p className="text-xs text-muted-foreground truncate">{apt.description}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {format(parseISO(apt.start_time), 'h:mm a')} - {format(parseISO(apt.end_time), 'h:mm a')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
        )}
      </Tabs>

      {/* Create Appointment Dialog - Styled like AI Personality popup */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="create-appointment-dialog">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Create Appointment</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {newAppointment.start_time ? format(parseISO(newAppointment.start_time), 'EEEE, MMMM d, yyyy') : 'Schedule a new appointment'}
            </p>
          </DialogHeader>
          <div className="px-4 pb-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="apt-title" className="text-xs font-medium">Title *</Label>
              <Input
                id="apt-title"
                placeholder="Meeting title"
                value={newAppointment.title}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, title: e.target.value }))}
                className="h-9 rounded-xl"
                data-testid="appointment-title-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="apt-desc" className="text-xs font-medium">Description</Label>
              <Input
                id="apt-desc"
                placeholder="Optional description"
                value={newAppointment.description}
                onChange={(e) => setNewAppointment(prev => ({ ...prev, description: e.target.value }))}
                className="h-9 rounded-xl"
                data-testid="appointment-desc-input"
              />
            </div>
            <div className="grid grid-cols-1 gap-2">
              <div className="space-y-1.5">
                <Label htmlFor="apt-start" className="text-xs font-medium">Start Time *</Label>
                <Input
                  id="apt-start"
                  type="datetime-local"
                  value={newAppointment.start_time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, start_time: e.target.value }))}
                  className="h-10 rounded-xl text-sm w-full"
                  style={{ fontSize: '16px' }}
                  data-testid="appointment-start-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="apt-end" className="text-xs font-medium">End Time *</Label>
                <Input
                  id="apt-end"
                  type="datetime-local"
                  value={newAppointment.end_time}
                  onChange={(e) => setNewAppointment(prev => ({ ...prev, end_time: e.target.value }))}
                  className="h-10 rounded-xl text-sm w-full"
                  style={{ fontSize: '16px' }}
                  data-testid="appointment-end-input"
                />
              </div>
            </div>
            <Button 
              className="w-full h-10 rounded-xl bg-[#586D52] hover:bg-[#4a5d46] mt-2" 
              onClick={handleCreateAppointment}
              disabled={loading}
              data-testid="save-appointment-btn"
            >
              {loading ? 'Creating...' : 'Create Appointment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Settings Dialog - Styled like AI Personality popup */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-testid="calendar-settings-dialog">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Calendar Settings</DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5">Connect your calendars for automatic sync</p>
          </DialogHeader>
          <div className="px-4 pb-4 space-y-3">
            <div className="p-3 border border-gray-100 dark:border-gray-800 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#0078d4">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-sm">Microsoft Calendar</h3>
                  <p className="text-xs text-muted-foreground">Sync with Outlook Calendar</p>
                </div>
                <Button variant="outline" size="sm" className="rounded-lg" data-testid="link-microsoft-btn">
                  <Link className="w-3.5 h-3.5 mr-1" />
                  Link
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center px-2">
              Linking your calendar will sync your appointments automatically.
            </p>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CalendarPage;
